import { TICK_RATE } from "./constants.ts";
import type { SlamRules } from "./combat.ts";

export const MatchPhase = {
  /** Lobby (GDD §6.3): free roaming and warm-up, no score; the host starts the match. */
  Lobby: "lobby",
  /** Synchronized roster presentation after the map title, before the fight. */
  Intro: "intro",
  /** Everyone on a spawn point, frozen, counting down. */
  Countdown: "countdown",
  /** Arena title card before the fighter cards and in-arena countdown. */
  MapIntro: "map-intro",
  Playing: "playing",
  /** Results on screen, then back to the lobby (the room stays: rematch). */
  Ended: "ended",
} as const;
export type MatchPhase = (typeof MatchPhase)[keyof typeof MatchPhase];
export const MATCH_INTRO_TICKS = 5 * TICK_RATE;
export const MAP_INTRO_TICKS = 3 * TICK_RATE;

/** Independent from free-for-all / teams: the host chooses both in the lobby. */
export const GameMode = { Slam: 0, KO: 1 } as const;
/**
 * Arena KO (D42): to the last blood. In a match nobody comes back: the last one
 * standing (or the last team) wins. If time runs out with several still up,
 * `overtimeTicks` of deathmatch follow; then everyone standing shares the draw.
 * Respawns remain in training, where nothing is at stake.
 */
export const KO_RULES = Object.freeze({
  maxHp: 500, respawnTicks: 3 * TICK_RATE, spawnShieldTicks: 45,
  fallDamage: 50, creditTicks: 5 * TICK_RATE, antiChainTicks: 24,
  overtimeTicks: 60 * TICK_RATE,
});

export const EndReason = {
  Score: "score",
  Time: "time",
  /** Not enough players left: the ones still in the room win by forfeit. */
  Abandon: "abandon",
  /** Arena KO: one fighter (or one team) left standing. */
  LastStanding: "last",
} as const;
export type EndReason = (typeof EndReason)[keyof typeof EndReason];

/**
 * Rules of a match, kept apart from the fighters (GDD §3.8) so later modes
 * (teams, lives, objectives) reuse the same combat engine.
 */
export interface MatchRules extends SlamRules {
  readonly minPlayers: number;
  readonly countdownTicks: number;
  readonly durationTicks: number;
  /** First to this score wins at once; 0 = only the timer ends the match. */
  readonly targetScore: number;
  readonly resultsTicks: number;
  /** Meter spent by a Super Slam. */
  readonly slamCost: number;
  /** Meter every fighter starts the match with. */
  readonly initialSlam: number;
  /** Points per opponent hit by one Super Slam (at most one hit per target). */
  readonly pointsPerTarget: number;
  /** After being hit by a Super Slam, further Super Slams pass through for this long. */
  readonly antiChainTicks: number;
  /** Team matches: points to win per player in the larger team (15 in 3v3, 20 in 4v4). */
  readonly teamPointsPerPlayer: number;
}

/** MVP duel (GDD §3.3 and the decisions in docs/DECISIONS.md, D9–D12). */
export const DUEL_RULES: MatchRules = Object.freeze({
  minPlayers: 2,
  countdownTicks: 3 * TICK_RATE,
  durationTicks: 180 * TICK_RATE,
  targetScore: 10,
  resultsTicks: 8 * TICK_RATE,
  slamMax: 100,
  slamCost: 100,
  initialSlam: 0,
  pointsPerTarget: 1,
  maxSlamGainPerEvent: 15,
  victimSlamFactor: 0.25,
  antiChainTicks: 3 * TICK_RATE,
  teamPointsPerPlayer: 5,
});

/** Returns a list of problems; empty means the rules are usable. */
export function validateRules(rules: MatchRules): string[] {
  const errors: string[] = [];
  const positive: (keyof MatchRules)[] = ["minPlayers", "durationTicks", "resultsTicks", "slamMax", "slamCost"];
  for (const key of positive) {
    if (!(Number(rules[key]) > 0)) errors.push(`${key} deve essere > 0`);
  }
  if (rules.countdownTicks < 0 || rules.countdownTicks > 255) errors.push("countdownTicks fuori da 0–255");
  if (rules.slamCost > rules.slamMax) errors.push("slamCost supera slamMax");
  if (rules.initialSlam < 0 || rules.initialSlam > rules.slamMax) errors.push("initialSlam fuori da 0–slamMax");
  if (rules.targetScore < 0) errors.push("targetScore negativo");
  if (!(rules.teamPointsPerPlayer > 0)) errors.push("teamPointsPerPlayer deve essere > 0");
  if (rules.victimSlamFactor < 0 || rules.victimSlamFactor > 1) errors.push("victimSlamFactor fuori da 0–1");
  return errors;
}

export interface Standing {
  readonly id: string;
  readonly score: number;
  /** Left the room before the end: ranked last, cannot win. */
  readonly left: boolean;
  /** Join order: the last tie-breaker, the same on every client. */
  readonly order: number;
}

/** Final ranking: score (descending), players who left last, then join order. */
export function rankStandings(standings: readonly Standing[]): Standing[] {
  return [...standings].sort(
    (a, b) => Number(a.left) - Number(b.left) || b.score - a.score || a.order - b.order,
  );
}

/**
 * Winner of a ranked list, or null for a draw. On a forfeit the best player
 * still in the room wins whatever the score; otherwise the top score must be
 * unique among the players still in the room.
 */
export function winnerOf(ranked: readonly Standing[], reason: EndReason): string | null {
  const present = ranked.filter((s) => !s.left);
  const first = present[0];
  if (!first) return null;
  if (reason === EndReason.Abandon) return first.id;
  const second = present[1];
  return second && second.score === first.score ? null : first.id;
}

/** A fighter at the end of an Arena KO match. */
export interface Survivor extends Standing {
  /** Still standing when the match ended. */
  readonly standing: boolean;
  /** Server tick of the elimination (later is better); 0 while standing. */
  readonly fell: number;
}

/**
 * Arena KO ranking: who is still standing first, then the fallen, the last to
 * fall first; KOs scored, then join order, settle the rest. Leavers last.
 */
export function rankSurvivors(survivors: readonly Survivor[]): Survivor[] {
  return [...survivors].sort(
    (a, b) => Number(a.left) - Number(b.left) || Number(b.standing) - Number(a.standing) ||
      b.fell - a.fell || b.score - a.score || a.order - b.order,
  );
}

/**
 * The last one standing, or null for a draw: several still up after the
 * deathmatch, or the last two falling on the same tick. A forfeit goes to the
 * best fighter who stayed.
 */
export function survivorWinner(ranked: readonly Survivor[], reason: EndReason): string | null {
  const present = ranked.filter((s) => !s.left);
  if (reason === EndReason.Abandon) return present[0]?.id ?? null;
  const standing = present.filter((s) => s.standing);
  if (standing.length > 0) return standing.length === 1 ? standing[0]!.id : null;
  const [last, next] = present;
  return last && (!next || next.fell !== last.fell) ? last.id : null;
}
